import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useAppData } from "../../../context/AppDataContext";
import { useAuthInfo } from "../../../context/AuthContext";
import { errorToast, successfulToast } from "../../../util/toastNotifications";
import Loading from "../../layout/Loading";
import Form from "../../layout/Form";
import FormFieldText from "../../input/FormFieldText";
import NovelEditor from "../../core/NovelEditor";
import ComboBox from "../../core/ComboBox";
import Button from "../../core/Button";
import TaskSelector from "../../sprint/components/TaskSelector";

const getInitialForm = () => {
  const today = new Date().toISOString().split("T")[0];
  return {
    name: "",
    description: "",
    start_date: today,
    end_date: "",
    project_ids: [],
    is_active: true,
  };
};

const SprintForm = () => {
  const { sprintId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { api } = useAppData();
  const { userInfo } = useAuthInfo();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState(getInitialForm());
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [errors, setErrors] = useState({});
  const [showProjectComboBox, setShowProjectComboBox] = useState(false);
  const hasSetProjectId = useRef(false);
  const projectComboBoxRef = useRef(null);

  useEffect(() => {
    hasSetProjectId.current = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsData, sprintData] = await Promise.all([
          api.project.getProjects({ _all: true, per_page: 100 }),
          sprintId ? api.sprint.getSprintById(sprintId) : null,
        ]);

        const activeProjects = projectsData
          .filter((project) => {
            if (project.is_active !== true) {
              return false;
            }
            const statusName =
              typeof project.status === "string"
                ? project.status
                : project.status?.name;
            if (statusName === "Cancelled" || statusName === "Complete") {
              return false;
            }
            return true;
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        setProjects(activeProjects);

        let sprintProjects = [];
        let projectIds = [];

        if (sprintData) {
          sprintProjects = await api.sprint.getProjectsBySprint(sprintId);
          projectIds = sprintProjects.map((p) => p.project_id);
          const endDate = new Date(sprintData.end_date);
          const today = new Date();
          const isActive = endDate >= today;

          const formatDateForInput = (dateString) => {
            if (!dateString) return "";
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          };

          const todayFormatted = formatDateForInput(today.toISOString());

          const newFormData = {
            name: sprintData.name,
            description: sprintData.description || "",
            start_date: sprintData.start_date
              ? formatDateForInput(sprintData.start_date)
              : todayFormatted,
            end_date: sprintData.end_date
              ? formatDateForInput(sprintData.end_date)
              : "",
            project_ids: projectIds,
            is_active: isActive,
          };
          setFormData(newFormData);

          const tasksData = await api.task.getTasks({
            include: ["assigned_users", "status"],
            sprint_id: sprintId,
          });

          const taskIds = tasksData.map((task) => task.task_id);
          setSelectedTasks(taskIds);
        } else {
          const params = new URLSearchParams(location.search);
          const projectId = params.get("project_id");
          if (projectId && !hasSetProjectId.current) {
            setFormData((prev) => ({
              ...prev,
              project_ids: [projectId],
            }));
            hasSetProjectId.current = true;
          }
        }
        const mergedProjectsMap = new Map();
        activeProjects.forEach((p) => mergedProjectsMap.set(p.project_id, p));
        sprintProjects.forEach((p) => mergedProjectsMap.set(p.project_id, p));

        setProjects(Array.from(mergedProjectsMap.values()));
      } catch (error) {
        console.error("Error fetching data:", error);
        errorToast("Failed to load sprint form. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, sprintId, location.search]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate < startDate) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    if (!formData.end_date) {
      newErrors.end_date = "End date is required";
    }

    if (!formData.project_ids || formData.project_ids.length === 0) {
      newErrors.project_ids = "At least one project is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const selectedProjects = projects.filter((p) =>
        formData.project_ids.includes(p.project_id)
      );
      if (selectedProjects.length === 0) {
        throw new Error("No valid projects selected");
      }

      const orgId = userInfo?.org_id;

      const endDate = new Date(formData.end_date + "T23:59:59");
      const today = new Date();
      const isActive = endDate >= today;

      const startDate = formData.start_date
        ? new Date(formData.start_date + "T00:00:00").toISOString()
        : new Date().toISOString();

      const sprintData = {
        name: formData.name,
        description: formData.description || null,
        org_id: orgId,
        created_by_id: userInfo?.user_id,
        start_date: startDate,
        project_ids: formData.project_ids || [],
        end_date: new Date(formData.end_date + "T23:59:59").toISOString(),
        is_active: isActive,
      };

      let targetSprintId = sprintId;
      if (sprintId) {
        await api.sprint.updateSprint(sprintId, sprintData);
        successfulToast("Sprint updated successfully");
      } else {
        const newSprint = await api.sprint.createSprint(sprintData);

        const newSprintId =
          newSprint.sprint_id || newSprint.id || newSprint._id;

        if (!newSprintId) {
          console.error("No sprint ID in response:", newSprint);
          throw new Error(
            "Failed to create sprint: No sprint ID returned from server"
          );
        }

        targetSprintId = newSprintId;
        successfulToast("Sprint created successfully");
      }

      await api.sprint.assignTasks(targetSprintId, selectedTasks);

      history.push(`/sprint/${targetSprintId}`);
    } catch (error) {
      console.error("Error saving sprint:", error);
      errorToast(error.message || "Failed to save sprint");
      setErrors({ submit: error.message || "Failed to save sprint" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === "project_ids") {
      setSelectedTasks([]);
    }
  };

  const handleProjectSelect = (projectId) => {
    setFormData((prev) => {
      const currentIds = prev.project_ids || [];
      if (currentIds.includes(projectId)) {
        return {
          ...prev,
          project_ids: currentIds.filter((id) => id !== projectId),
        };
      } else {
        return {
          ...prev,
          project_ids: [...currentIds, projectId],
        };
      }
    });
    if (errors.project_ids) {
      setErrors((prev) => ({ ...prev, project_ids: null }));
    }
  };

  const handleProjectRemove = (projectId) => {
    setFormData((prev) => ({
      ...prev,
      project_ids: (prev.project_ids || []).filter((id) => id !== projectId),
    }));
    setSelectedTasks([]);
  };

  const formatProjectForDisplay = (project) => project.name;

  const handleTaskSelection = useCallback((taskIds) => {
    setSelectedTasks(taskIds);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        projectComboBoxRef.current &&
        !projectComboBoxRef.current.contains(event.target)
      ) {
        setShowProjectComboBox(false);
      }
    };

    if (showProjectComboBox) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProjectComboBox]);

  const selectedProjects = projects.filter((p) =>
    (formData.project_ids || []).includes(p.project_id)
  );

  if (loading && sprintId) {
    return <Loading />;
  }

  return (
    <div className="form-styling px-16 py-8 card">
      <h2>{sprintId ? "Edit Sprint" : "New Sprint"}</h2>

      <Form onSubmit={handleSubmit}>
        {errors.submit && (
          <div className="alert alert-danger" role="alert">
            {errors.submit}
          </div>
        )}

        <FormFieldText
          name="name"
          label="Sprint Name *"
          placeholder="Enter sprint name..."
          value={formData.name}
          onChange={handleInputChange}
          errorMessage={errors.name}
        />

        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="start_date">Start Date</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              className={`form-control ${
                errors.start_date ? "is-invalid" : ""
              }`}
            />
            {errors.start_date && (
              <div className="invalid-feedback">{errors.start_date}</div>
            )}
          </div>

          <div className="form-group col-md-6">
            <label htmlFor="end_date">End Date *</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              className={`form-control ${errors.end_date ? "is-invalid" : ""}`}
            />
            {errors.end_date && (
              <div className="invalid-feedback">{errors.end_date}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="project_ids">Projects *</label>
          <div ref={projectComboBoxRef} style={{ position: "relative" }}>
            {selectedProjects.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                {selectedProjects.map((project) => (
                  <span
                    key={project.project_id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: "32px",
                      padding: "0 8px",
                      backgroundColor: "var(--input-background)",
                      border: "1px solid var(--input-border)",
                      borderRadius: "6px",
                      fontSize: "0.875rem",
                      color: "var(--input-color)",
                    }}
                  >
                    {project.name}
                    <button
                      type="button"
                      onClick={() => handleProjectRemove(project.project_id)}
                      style={{
                        marginLeft: "6px",
                        justifyContent: "flex-end",
                        background: "none",
                        border: "none",
                        color: "var(--input-color)",
                        cursor: "pointer",
                        padding: "0",
                        fontSize: "1rem",
                        lineHeight: "1",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div
              className={`form-control ${
                errors.project_ids ? "is-invalid" : ""
              }`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
              onClick={() => setShowProjectComboBox(!showProjectComboBox)}
            >
              <span
                style={{
                  color:
                    selectedProjects.length > 0
                      ? "var(--input-color)"
                      : "var(--input-disabled-color, #999)",
                  flex: 1,
                }}
              >
                {selectedProjects.length > 0
                  ? `Select projects (${selectedProjects.length} selected)`
                  : "Select projects"}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  color: "var(--input-color)",
                  fontSize: "0.75rem",
                  marginLeft: "8px",
                  transition: "transform 0.2s",
                  transform: showProjectComboBox
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                }}
              />
            </div>
            {showProjectComboBox && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  marginTop: "4px",
                }}
              >
                <ComboBox
                  data={projects}
                  selected={selectedProjects}
                  valueKey="project_id"
                  placeholder="Search projects..."
                  searchTopic="project"
                  formatData={formatProjectForDisplay}
                  handleSelect={handleProjectSelect}
                  onRequestClose={() => setShowProjectComboBox(false)}
                  inline={false}
                  allowMultiple={true}
                />
              </div>
            )}
            {errors.project_ids && (
              <div className="invalid-feedback">{errors.project_ids}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <NovelEditor
            initialValue={
              formData.description
                ? (() => {
                    try {
                      return JSON.parse(formData.description);
                    } catch {
                      return null;
                    }
                  })()
                : null
            }
            onChange={(jsonContent) => {
              setFormData((prev) => ({
                ...prev,
                description: JSON.stringify(jsonContent),
              }));
            }}
          />
        </div>

        <div className="button-row">
          <div className="button-row-left">
            <Button cancel onClick={() => history.goBack()}>
              Cancel
            </Button>
          </div>
          <div className="button-row-right">
            <button type="submit" disabled={loading}>
              {loading
                ? "Saving..."
                : sprintId
                  ? "Update Sprint"
                  : "Create Sprint"}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default SprintForm;
